import React from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {NavStoreProject, ProjectSummary} from 'constants/SourceConstants';
import EntityStore from 'stores/EntityStore';

import CreateOrganizationPage from './CreateOrganizationPage';
import CreateTeamPage from './CreateTeamPage';
import {
  CREATE_ORGANIZATION_SCREEN,
  CREATE_TEAM_SCREEN,
  ORGANIZATION_SCREEN,
  SHARE_PROJECTS_SCREEN,
  TEAM_SCREEN,
  USER_SCREEN,
  OrganizationScreen,
  Organization,
  Team,
  Member,
  MemberInfo,
  MemberRequest,
  MemberInvite,
  OrganizationPageTab,
} from './OrganizationConstants';
import OrganizationPage from './OrganizationPage';
import PartyPageStore from './PartyPageStore';
import ShareProjectsPage from './ShareProjectsPage';
import TeamPage from './TeamPage';
import UserPage from './UserPage';

interface PartyFullScreenViewProps {
  currentScreen: OrganizationScreen;
}

/**
 * All of these are typed as | null because the are sometimes null in `PartyPageStore`. They _should_ be
 * populated contextually when looking at any given tab.
 */
interface EnhancedProps {
  activeTab: OrganizationPageTab | null;
  isLoading: boolean;
  /*
   * should be {name: string} for the create organization flow, null in Team related flows, full Organization
   * otherwise
   */
  organization: Organization | {name: string} | null;
  organizationMemberInvites: Array<MemberInvite> | null;
  organizationMemberRequests: Array<MemberRequest> | null;
  organizationMembers: Array<Member> | null;
  organizationProjects: Array<NavStoreProject> | null;
  organizationTeams: Array<Team> | null;
  team: Team | null;
  teamMembers: Array<Member> | null;
  teamProjects: Array<ProjectSummary> | null;
  userIdToMemberInfo: Record<string, MemberInfo> | null;
  userId: string | null;
}

class PartyFullScreenViewConnector extends DataConnector<PartyFullScreenViewProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [EntityStore, PartyPageStore];

  enhanceProps(): PartyFullScreenViewProps & EnhancedProps {
    const organization = this.partyPageStore().getCurrentOrganization();
    const team = this.partyPageStore().getCurrentTeam();
    let organizationMembers = null;
    if (organization) {
      organizationMembers = this.entityStore().getMembersForGroup((organization as Organization).id);
    }
    let teamMembers = null;
    if (team) {
      teamMembers = this.entityStore().getMembersForGroup(team.id);
    }
    return {
      activeTab: this.partyPageStore().getActiveTab(),
      currentScreen: this.props.currentScreen,
      isLoading: this.partyPageStore().getIsLoading(),
      organization,
      organizationMemberInvites: this.partyPageStore().getOrganizationMemberInvites(),
      organizationMemberRequests: this.partyPageStore().getOrganizationMemberRequests(),
      organizationMembers,
      organizationProjects: this.partyPageStore().getOrganizationProjects(),
      organizationTeams: this.partyPageStore().getOrganizationTeams(),
      team,
      teamMembers,
      teamProjects: this.partyPageStore().getTeamProjects(),
      userIdToMemberInfo: this.partyPageStore().getUserIdToMemberInfo(),
      userId: this.partyPageStore().getUserPageUserId(),
    };
  }
}

function PartyFullScreenView(props: PartyFullScreenViewProps & EnhancedProps): JSX.Element {
  switch (props.currentScreen) {
    case ORGANIZATION_SCREEN:
      return (
        <OrganizationPage
          activeTab={props.activeTab!}
          isLoading={props.isLoading}
          deprecatedOrganization={props.organization! as Organization}
          organizationMemberInvites={props.organizationMemberInvites!}
          organizationMemberRequests={props.organizationMemberRequests!}
          organizationMembers={props.organizationMembers!}
          organizationProjects={props.organizationProjects!}
          organizationTeams={props.organizationTeams!}
          userIdToMemberInfo={props.userIdToMemberInfo!}
        />
      );
    case TEAM_SCREEN:
      return (
        <TeamPage
          activeTab={props.activeTab!}
          isLoading={props.isLoading}
          deprecatedTeam={props.team!}
          teamMembers={props.teamMembers!}
          teamProjects={props.teamProjects!}
          userIdToMemberInfo={props.userIdToMemberInfo}
        />
      );
    case USER_SCREEN:
      return <UserPage activeTab={props.activeTab!} userId={props.userId!} />;
    case CREATE_ORGANIZATION_SCREEN:
      return <CreateOrganizationPage organization={props.organization!} isLoading={props.isLoading} />;
    case CREATE_TEAM_SCREEN:
      return (
        <CreateTeamPage
          organizationId={(props.organization! as Organization).id}
          isLoading={props.isLoading}
        />
      );
    case SHARE_PROJECTS_SCREEN:
      return (
        <ShareProjectsPage
          organizationId={(props.organization! as Organization).id}
          isLoading={props.isLoading}
        />
      );
    default:
      assert(false, `OrganizationView called with invalid screen: ${props.currentScreen}`);
  }
}

export default PartyFullScreenViewConnector.connect(PartyFullScreenView);
